import { useEffect, useRef, useState } from "react";
import type { Character, Message, Room } from "@/lib/api";
import { api, clearAppToken, deleteAvatar, deleteMessage, deleteRoom, deleteTurn, editTurn, getAppToken, post, resetRoom, setAppToken, streamChat, uploadAvatar } from "@/lib/api";
import { Sidebar } from "@/Sidebar";
import { NewRoomModal } from "@/NewRoomModal";
import { MainArea } from "@/MainArea";
import { ProviderSettingsModal } from "@/ProviderSettingsModal";
import { RoomActionsModal } from "@/RoomActionsModal";
import { characterXmlTemplate, parseCharacterXml } from "@/lib/characterXml";

export type { Character, Room } from "@/lib/api";

export default function App() {
  const [chars, setChars] = useState<Character[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [activeRoom, setActiveRoom] = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<"chat" | "chars">("chat");
  const [editing, setEditing] = useState<Character | null>(null);
  const [isNewRoom, setIsNewRoom] = useState(false);
  const [roomName, setRoomName] = useState("");
  const [roomScenario, setRoomScenario] = useState("");
  const [roomPick, setRoomPick] = useState<Set<number>>(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [startupError, setStartupError] = useState<string | null>(null);
  const [providerSettingsOpen, setProviderSettingsOpen] = useState(false);
  const [roomActionsOpen, setRoomActionsOpen] = useState(false);
  const [appToken, setAppTokenState] = useState(() => getAppToken());
  const [tokenDraft, setTokenDraft] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  const charById = new Map(
    chars.filter((c) => c.id != null).map((c) => [c.id, c] as const)
  );
  const activeCast = activeRoom
    ? (rooms.find((r) => r.id === activeRoom)?.character_ids ?? [])
        .map((id) => charById.get(id))
        .filter((c) => c != null)
    : [];
  const activeRoomData = rooms.find((room) => room.id === activeRoom);

  const refresh = async () => {
    try {
      const c = (await api("/characters")) as { characters: Character[] };
      const r = (await api("/rooms")) as { rooms: Room[] };
      setChars(c.characters ?? []);
      const rms = r.rooms ?? [];
      setRooms(rms);
      if (rms.length && !rms.some((x) => x.id === activeRoom)) setActiveRoom(rms[0].id);
      setStartupError(null);
    } catch (error) {
      setStartupError(error instanceof Error ? error.message : "Unable to load the backend");
    }
  };

  useEffect(() => {
    if (!appToken) return;
    void refresh();
  }, [appToken]);

  useEffect(() => {
    const handleAuthExpired = () => {
      setAppTokenState("");
      setStartupError(null);
    };
    window.addEventListener("k6waifus-auth-expired", handleAuthExpired);
    return () => window.removeEventListener("k6waifus-auth-expired", handleAuthExpired);
  }, []);

  useEffect(() => {
    void (async () => {
      if (!activeRoom) return;
      try {
        const r = await api(`/rooms/${activeRoom}/messages`);
        setMessages(r.messages ?? []);
      } catch (error) {
        setStartupError(error instanceof Error ? error.message : "Unable to load messages");
      }
    })();
  }, [activeRoom]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  async function send(textOverride?: string) {
    const text = (textOverride ?? input).trim();
    if (!text || loading || !activeRoom) return;
    setInput("");
    const draftId = `draft${Date.now()}`;
    setMessages((p) => [...p, { id: `p${Date.now()}`, role: "user", content: text, character_id: null }, { id: draftId, role: "assistant", content: "", character_id: activeCast[0]?.id ?? null, is_draft: true }]);
    setLoading(true);
    try {
      const data = await streamChat(activeRoom, text, (delta) => {
        setMessages((current) => current.map((item) => item.id === draftId ? { ...item, content: item.content + delta } : item));
      });
      const replies = data.replies ?? [];
      setMessages((p) => [...p.filter((item) => item.id !== draftId), ...replies]);
    } catch (error) {
      const details = error as Error & { data?: { failed_message_id?: number } };
      setMessages((p) => [
        ...p.filter((item) => item.id !== draftId),
        {
          id: `e${Date.now()}`,
          role: "assistant",
          content: "The provider did not respond. You can try sending the message again or delete this message.",
          character_id: null,
          is_error: true,
          retry_text: text,
          source_message_id: details.data?.failed_message_id
        }
      ]);
    } finally {
      setLoading(false);
    }
  }

  function removeFailedFromView(message: Message) {
    setMessages((current) => {
      const errorIndex = current.findIndex((item) => item.id === message.id);
      let userIndex = -1;
      for (let index = errorIndex - 1; index >= 0; index -= 1) {
        const item = current[index];
        if (item.role === "user" && item.content === message.retry_text) {
          userIndex = index;
          break;
        }
      }
      return current.filter((item, index) => index !== errorIndex && index !== userIndex);
    });
  }

  async function removeFailedMessage(message: Message) {
    if (activeRoom && typeof message.source_message_id === "number") {
      await deleteMessage(activeRoom, message.source_message_id);
    }
    removeFailedFromView(message);
  }

  async function retryFailedMessage(message: Message) {
    if (activeRoom && typeof message.source_message_id === "number") {
      await deleteMessage(activeRoom, message.source_message_id);
    }
    removeFailedFromView(message);
    await send(message.retry_text);
  }

  async function removeTurn(message: Message) {
    if (!activeRoom || typeof message.turn_id !== "number") return;
    try {
      await deleteTurn(activeRoom, message.turn_id);
      setMessages((current) => current.filter((item) => item.turn_id !== message.turn_id));
    } catch (error) {
      setStartupError(error instanceof Error ? error.message : "Unable to delete turn");
    }
  }

  async function editMessage(message: Message) {
    if (!activeRoom || typeof message.turn_id !== "number") return;
    const next = window.prompt("Edit pesan", message.content)?.trim();
    if (!next || next === message.content) return;
    const draftId = `edit${Date.now()}`;
    setMessages((current) => [
      ...current.filter((item) => item.turn_id !== message.turn_id),
      { ...message, content: next },
      { id: draftId, role: "assistant", content: "", character_id: activeCast[0]?.id ?? null, turn_id: message.turn_id, is_draft: true }
    ]);
    setLoading(true);
    try {
      const data = await editTurn(activeRoom, message.turn_id, next, (delta) => {
        setMessages((current) => current.map((item) => item.id === draftId ? { ...item, content: item.content + delta } : item));
      });
      setMessages((current) => [...current.filter((item) => item.id !== draftId), ...(data.replies ?? [])]);
    } catch (error) {
      setStartupError(error instanceof Error ? error.message : "Unable to regenerate message");
    } finally {
      setLoading(false);
    }
  }

  async function resetActiveRoom() {
    if (!activeRoom) return;
    try {
      await resetRoom(activeRoom);
      setMessages([]);
      setRoomActionsOpen(false);
    } catch (error) {
      setStartupError(error instanceof Error ? error.message : "Unable to reset chat");
    }
  }

  async function deleteActiveRoom() {
    if (!activeRoom) return;
    try {
      await deleteRoom(activeRoom);
      setMessages([]);
      setRoomActionsOpen(false);
      setActiveRoom(null);
      await refresh();
    } catch (error) {
      setStartupError(error instanceof Error ? error.message : "Unable to delete chat");
    }
  }

  async function importCharacterXml(file: File) {
    if (file.size > 512 * 1024) {
      setStartupError("XML file is too large. Maximum 512 KB.");
      return;
    }
    try {
      const imported = parseCharacterXml(await file.text());
      setEditing(imported);
      setView("chars");
      setStartupError(null);
    } catch (error) {
      setStartupError(error instanceof Error ? error.message : "Character XML is invalid");
    }
  }

  function downloadCharacterTemplate() {
    const blob = new Blob([characterXmlTemplate()], { type: "application/xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "character-template.xml";
    link.click();
    URL.revokeObjectURL(url);
  }

  async function saveChar() {
    if (!editing) return;
    try {
      const payload = { ...editing, name: editing.name.trim() || "Unknown" };
      if (editing.id) {
        await api(`/characters/${editing.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
      } else {
        await post("/characters", payload);
      }
      setEditing(null);
      await refresh();
    } catch (error) {
      setStartupError(error instanceof Error ? error.message : "Unable to save character");
    }
  }

  async function handleAvatarUpload(file: File) {
    if (!editing?.id) return;
    try {
      const result = await uploadAvatar(editing.id, file);
      setEditing({ ...editing, avatar_key: result.avatar_key });
      await refresh();
    } catch (error) {
      setStartupError(error instanceof Error ? error.message : "Unable to upload avatar");
    }
  }

  async function handleAvatarRemove() {
    if (!editing?.id) return;
    try {
      await deleteAvatar(editing.id);
      setEditing({ ...editing, avatar_key: null });
      await refresh();
    } catch (error) {
      setStartupError(error instanceof Error ? error.message : "Unable to remove avatar");
    }
  }

  async function deleteCharacter(character: Character) {
    if (!character.id) return;
    try {
      await api(`/characters/${character.id}`, { method: "DELETE" });
      setEditing(null);
      await refresh();
    } catch (error) {
      setStartupError(error instanceof Error ? error.message : "Unable to delete character");
    }
  }

  async function createRoom() {
    try {
      await post("/rooms", { name: roomName.trim() || "New Room", scenario: roomScenario.trim(), characterIds: [...roomPick] });
      setRoomName("");
      setRoomScenario("");
      setRoomPick(new Set());
      setIsNewRoom(false);
      await refresh();
    } catch (error) {
      setStartupError(error instanceof Error ? error.message : "Unable to create room");
    }
  }

  const editField = (key: keyof Character, value: string) => {
    if (!editing) return;
    const arrayFields: Array<keyof Character> = ["traits", "speech_style", "boundaries"];
    const nextValue = key === "example_dialogs"
      ? JSON.parse(value) as Character["example_dialogs"]
      : arrayFields.includes(key)
      ? value.split(",").map((item) => item.trim()).filter(Boolean)
      : value;
    setEditing({ ...editing, [key]: nextValue } as Character);
  };

  if (!appToken) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <form className="w-full max-w-sm space-y-4 rounded-2xl border bg-card p-6 shadow-xl" onSubmit={(event) => {
          event.preventDefault();
          const token = tokenDraft.trim();
          if (!token) return;
          setAppToken(token);
          setAppTokenState(token);
          setTokenDraft("");
        }}>
          <div>
            <h1 className="text-lg font-semibold">Sign in to k6waifus</h1>
            <p className="mt-1 text-sm text-muted-foreground">Enter the app token to continue.</p>
          </div>
          <input autoFocus type="password" value={tokenDraft} onChange={(event) => setTokenDraft(event.target.value)} className="w-full rounded-md border bg-background px-3 py-2 text-sm" aria-label="App token" />
          <button type="submit" className="w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground">Continue</button>
        </form>
      </div>
    );
  }

  return (
    <div className="app-shell-height flex w-full">
      {startupError && (
        <div className="fixed inset-x-0 top-0 z-[60] border-b border-red-400/30 bg-red-950/95 px-4 py-2 text-center text-xs text-red-100">
          Backend error: {startupError}
        </div>
      )}
      <button type="button" className="sr-only" onClick={() => { clearAppToken(); setAppTokenState(""); }}>Sign out</button>
      <Sidebar
        rooms={rooms}
        activeRoom={activeRoom}
        view={view}
        activeCast={activeCast as Character[]}
        onSelectRoom={(id) => {
          setActiveRoom(id);
          setView("chat");
        }}
        onNewRoom={() => setIsNewRoom(true)}
        onToggleChars={() => setView(view === "chars" ? "chat" : "chars")}
        onOpenProviderSettings={() => setProviderSettingsOpen(true)}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <MainArea
        view={view}
        activeRoom={activeRoom}
        rooms={rooms}
        messages={messages}
        loading={loading}
        input={input}
        chars={chars}
        editing={editing}
        charById={charById}
        setInput={setInput}
        setEditing={setEditing}
        send={send}
        saveChar={saveChar}
        editField={editField}
        endRef={endRef}
        onOpenSidebar={() => setSidebarOpen(true)}
        onAvatarUpload={handleAvatarUpload}
        onAvatarRemove={handleAvatarRemove}
        onDeleteCharacter={deleteCharacter}
        onRetryMessage={retryFailedMessage}
        onDeleteMessage={removeFailedMessage}
        onDeleteTurn={removeTurn}
        onEditMessage={editMessage}
        onOpenRoomActions={() => setRoomActionsOpen(true)}
        onImportXml={importCharacterXml}
        onDownloadTemplate={downloadCharacterTemplate}
      />
      {isNewRoom && (
        <NewRoomModal
          chars={chars}
          name={roomName}
          scenario={roomScenario}
          pick={roomPick}
          onName={setRoomName}
          onScenario={setRoomScenario}
          onPick={setRoomPick}
          onCancel={() => setIsNewRoom(false)}
          onCreate={() => void createRoom()}
        />
      )}
      {providerSettingsOpen && <ProviderSettingsModal onClose={() => setProviderSettingsOpen(false)} />}
      {roomActionsOpen && activeRoom && activeRoomData && (
        <RoomActionsModal
          roomName={activeRoomData.name}
          onClose={() => setRoomActionsOpen(false)}
          onReset={resetActiveRoom}
          onDelete={deleteActiveRoom}
        />
      )}
    </div>
  );
}